import TicketAssigneeBadge from "../TicketAssigneeBadge";
import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";

import { parseISO, format, isSameDay } from "date-fns";
import {
  ListItem, ListItemText, ListItemAvatar, Typography, Avatar,
  Tooltip, IconButton, CircularProgress, SvgIcon
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Done, Close, Replay, Android, SwapHoriz, CloudUpload, ContactPhone } from '@material-ui/icons';
// import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop'; // Removed
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import RegisterLossModal from "../RegisterLossModal";
import ContactAvatar from "../ContactAvatar";
import clsx from "clsx";
import { toast } from "react-toastify";
import {
  MEDIA_DRAG_MIME_TYPE,
  MEDIA_DRAG_EVENT_START,
  MEDIA_DRAG_EVENT_END,
  MEDIA_DRAG_HANDLED_IN_APP,
  getMediaLabel,
} from "../../utils/mediaDrag";
import { normalizeFileName, ensureFileNameHasExtension } from "../../utils/fileName";
import { useDraftMessages } from "../../context/Drafts/DraftMessagesContext";
import MarkdownWrapper from "../MarkdownWrapper";
import { getTaskStatus } from "../../utils/taskStatus";
import { getPinModeForUser } from "../../utils/pinnedTickets";
import {
  getOutcomeBoardsFromTicket,
  getPrimaryOutcomeBoardId,
  hasOutcomeTrackingTicket
} from "../../utils/commercialOutcome";
import invalidateDashboardCache from "../../utils/invalidateDashboardCache";
import resolveTicketDisplayAt from "../../utils/ticketDisplayAt";
import { useCommercialCapabilities } from '../../context/Commercial/CommercialContext';
import CommercialQuickActions from '../CommercialQuickActions';
import TicketBadgeList from './TicketBadgeList';
import TicketQuickActions from './TicketQuickActions';

const brokenAvatarReports = new Map();

const useStyles = makeStyles((theme) => ({
  ticket: {
    position: "relative",
    padding: "5px 12px",
    cursor: "pointer",
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.palette.type === 'dark'
        ? theme.palette.grey[900]
        : theme.palette.action.hover
    },
    "&.selected": {
      backgroundColor: theme.palette.type === 'dark'
        ? `${theme.palette.primary.dark}20`
        : `${theme.palette.primary.main}10`,
      borderLeft: `3px solid ${theme.palette.primary.main}`
    },
    "& .MuiListItemText-multiline": {
      paddingBottom: 3,
    },
    minHeight: 50
  },
  ticketDragOver: {
    position: "relative",
    backgroundColor: theme.palette.type === "dark" ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.04)",
    borderRadius: 12,
    transition: "all 0.15s ease",
  },
  ticketDropDisabled: {
    opacity: 0.6,
    filter: "grayscale(0.3)",
  },
  ticketDragOverlay: {
    position: "absolute",
    top: 2,
    bottom: 2,
    left: 2,
    right: 2,
    borderRadius: 12,
    border: "2px dashed #14A45C",
    backgroundColor: theme.palette.type === "dark" ? "rgba(18, 18, 18, 0.85)" : "rgba(255, 255, 255, 0.85)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#14A45C",
    pointerEvents: "none",
    textAlign: "center",
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  customBadge: {
    position: "absolute",
    // Posição relativa ao centro do avatar (19px) com offset para canto superior direito
    top: "-1px",
    right: "3px",
    backgroundColor: "#f44336",
    color: "white",
    height: 16,
    minWidth: 16,
    fontSize: "0.65rem",
    fontWeight: 700,
    boxShadow: "0 2px 4px rgba(244, 67, 54, 0.4)",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 3px",
    zIndex: 1
  },
  manualFlagBadge: {
    position: "absolute",
    top: "-1px",
    right: "3px",
    backgroundColor: "#f44336",
    color: "white",
    height: 16,
    minWidth: 16,
    fontSize: "0.65rem",
    fontWeight: 700,
    boxShadow: "0 2px 4px rgba(244, 67, 54, 0.4)",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 3px",
    zIndex: 1
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    boxShadow: theme.palette.type === 'dark'
      ? "0 4px 10px rgba(0,0,0,0.5)"
      : "0 4px 10px rgba(0,0,0,0.15)",
    border: theme.palette.type === 'dark'
      ? "2px solid #2f2f2f"
      : "2px solid #fff",
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "scale(1.05)",
    }
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2
  },
  contactName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    display: "flex",
    alignItems: "center",
    gap: 0,
    lineHeight: 1.2
  },
  time: {
    fontSize: "0.8rem",
    color: theme.palette.text.disabled
  },
  lastMessage: {
    fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.2
  },
  searchHighlight: {
    backgroundColor: `${theme.palette.primary.main}26`,
    color: theme.palette.primary.main,
    borderRadius: 4,
    padding: "0 2px",
    fontWeight: 700,
  },
  draftMessage: {
    color: theme.palette.text.primary,
  },
  draftLabel: {
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginRight: 4,
  },
  draftPreview: {
    color: theme.palette.text.primary,
  },
  tags: {
    display: "flex",
    gap: 3,
    marginTop: 3,
    flexWrap: "nowrap",
    overflow: "hidden",
    minWidth: 0,
  },
  tag: {
    fontSize: "0.7rem",
    padding: "1px 4px",
    borderRadius: 4,
    fontWeight: 600,
    lineHeight: 1.3,
    display: "inline-block",
    whiteSpace: "nowrap",
    flexShrink: 0,
    border: theme.palette.type === 'dark' ? "1px solid rgba(255,255,255,0.1)" : "none"
  },
  moreTag: {
    cursor: "pointer",
    userSelect: "none",
    fontFamily: "inherit",
  },
  tagMeasure: {
    position: "absolute",
    visibility: "hidden",
    pointerEvents: "none",
    width: 0,
    height: 0,
    overflow: "hidden",
    top: 0,
    left: 0,
  },
  tagMeasureContent: {
    display: "flex",
    gap: 3,
    width: "max-content",
  },
  hiddenTagsPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#ffffff",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: "6px 8px",
    boxShadow: theme.palette.type === "dark"
      ? "0px 4px 20px rgba(0, 0, 0, 0.6)"
      : "0px 4px 20px rgba(0, 0, 0, 0.15)",
    maxWidth: 280,
  },
  hiddenTagsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  actions: {
    position: "absolute",
    right: 6,
    bottom: 6,
    display: "flex",
    gap: 3,
    // Round like the desktop's, with more room for a finger: won and lost sit side by side.
    'html[data-page-header="tablet"] &': {
      "--ticket-action-gap": "8px",
      "& .MuiIconButton-root": {
        position: "relative",
        width: 28,
        minWidth: 28,
        height: 28,
        minHeight: 28,
        padding: 6,
        "&::after": { content: '""', position: "absolute", inset: "-8px -4px" },
      },
    },
  },
  actionBtn: {
    ...theme.neoUI?.buttons?.action?.root,
    padding: 4,
    minHeight: 24,
    height: 24,
    minWidth: 24,
    width: 24,
    borderRadius: "50%",
    [theme.breakpoints.down("sm")]: {
      padding: 6,
      minHeight: "auto",
      height: "auto",
      minWidth: "auto",
      width: "auto",
    },
    "&:hover": {
      ...theme.neoUI?.buttons?.action?.root?.["&:hover"],
      color: "white !important",
      "& .MuiSvgIcon-root": {
        color: "white !important"
      }
    },
    "& .MuiSvgIcon-root": {
      color: "white",
    }
  },
  transferBtn: {
    ...theme.neoUI?.buttons?.action?.root,
    padding: 4,
    minHeight: 24,
    height: 24,
    minWidth: 24,
    width: 24,
    borderRadius: "50%",
    [theme.breakpoints.down("sm")]: {
      padding: 6,
      minHeight: "auto",
      height: "auto",
      minWidth: "auto",
      width: "auto",
    },
    "&:hover": {
      ...theme.neoUI?.buttons?.action?.root?.["&:hover"],
      color: "white !important",
      "& .MuiSvgIcon-root": {
        color: "white !important"
      }
    },
    "& .MuiSvgIcon-root": {
      color: "white",
    }
  },
  acceptBtn: {
    ...theme.neoUI?.buttons?.action?.root,
    padding: 4,
    minHeight: 24,
    height: 24,
    minWidth: 24,
    width: 24,
    borderRadius: "50%",
    [theme.breakpoints.down("sm")]: {
      padding: 6,
      minHeight: "auto",
      height: "auto",
      minWidth: "auto",
      width: "auto",
    },
    "&:hover": {
      ...theme.neoUI?.buttons?.action?.root?.["&:hover"],
      color: "white !important",
      "& .MuiSvgIcon-root": {
        color: "white !important"
      }
    },
    "& .MuiSvgIcon-root": {
      color: "white",
    }
  },
  icon: {
    fontSize: "1rem"
  },
  androidIcon: {
    fontSize: "0.9rem",
    color: "#ff9800",
    marginLeft: 4
  },
  listItemAvatar: {
    position: "relative",
    marginRight: 9, // Gap de 9px explicitado
  },
  "@keyframes glowRed": {
    "0%": { boxShadow: "0 0 0 1.5px #f44336, 0 0 5px #f44336" },
    "50%": { boxShadow: "0 0 0 1.5px #f44336, 0 0 12.5px #f44336" },
    "100%": { boxShadow: "0 0 0 1.5px #f44336, 0 0 5px #f44336" },
  },
  "@keyframes glowYellow": {
    "0%": { boxShadow: "0 0 0 1.5px #e4bc04, 0 0 5px #e4bc04" },
    "50%": { boxShadow: "0 0 0 1.5px #e4bc04, 0 0 12.5px #e4bc04" },
    "100%": { boxShadow: "0 0 0 1.5px #e4bc04, 0 0 5px #e4bc04" },
  },
  avatarPulsingRed: {
    animation: "$glowRed 1s infinite ease-in-out",
    borderRadius: "50%",
  },
  avatarPulsingYellow: {
    animation: "$glowYellow 1s infinite ease-in-out",
    borderRadius: "50%",
  },
  avatarBadge: {
    position: "absolute",
    bottom: -4,
    right: -4,
    width: 24,
    height: 24,
    fontSize: "0.70rem",
    border: theme.palette.type === 'dark' ? "2px solid #1c1c1c" : "2px solid #fff",
    borderRadius: "50%",
    boxShadow: theme.palette.type === 'dark'
      ? "0 2px 6px rgba(0,0,0,0.6)"
      : "0 2px 6px rgba(0,0,0,0.2)",
    boxSizing: "content-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2, // Ensure it sits on top of the main avatar
  },
  groupBadgeIcon: {
    width: 18,
    height: 18,
    display: "block",
  },
}));

const PushPinIcon = ({ outlined = false, htmlColor, ...props }) => (
  <SvgIcon {...props}>
    <path
      d="M16 12V4H17V2H7V4H8V12L6 14V16H11.2V22H12.8V16H18V14L16 12Z"
      fill={outlined ? "none" : htmlColor || "currentColor"}
      stroke={outlined ? htmlColor || "currentColor" : "none"}
      strokeWidth={outlined ? 1.8 : 0}
      strokeLinejoin="round"
    />
  </SvgIcon>
);

const TicketListItemCustom = ({ ticket, status, searchMatch, avatarRefreshEnabled = false }) => {
  const commercialEnabled = useCommercialCapabilities();
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { ticketId } = useParams();
  const { user } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { getDraft } = useDraftMessages();
  const isAdmin = (user?.profile || "").toUpperCase() === "ADMIN";
  const canViewPendingTickets = user?.viewPendingTickets === "enabled";
  const canOpenPendingTickets = isAdmin || canViewPendingTickets;

  const [loading, setLoading] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [lossModalOpen, setLossModalOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDraggingMedia, setIsDraggingMedia] = useState(false);
  const [draggingFromTicketId, setDraggingFromTicketId] = useState(null);
  const actionsRef = useRef(null);

  const pinStatus = typeof status === "string" ? status : null;
  const canPin = Boolean(pinStatus);
  const pinModeForUser = canPin
    ? getPinModeForUser(ticket.isPinned, pinStatus, user.id)
    : "none";
  const [localPinMode, setLocalPinMode] = useState(pinModeForUser);
  const [localManualFlag, setLocalManualFlag] = useState(ticket.manualFlag || false);
  const pinClickTimerRef = useRef(null);
  const pinnedFromModeRef = useRef(null);
  const mediaCacheRef = useRef(new Map());
  const outcomeBoards = useMemo(() => getOutcomeBoardsFromTicket(ticket), [ticket]);
  const hasOutcomeTracking = hasOutcomeTrackingTicket(ticket);
  const primaryOutcomeBoardId = getPrimaryOutcomeBoardId(ticket);

  useEffect(() => {
    setLocalPinMode(pinModeForUser);
    if (pinModeForUser !== "pinned") {
      pinnedFromModeRef.current = null;
    }
  }, [pinModeForUser]);

  useEffect(() => {
    return () => {
      if (pinClickTimerRef.current) {
        clearTimeout(pinClickTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setLocalManualFlag(ticket.manualFlag || false);
  }, [ticket.manualFlag]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return () => { };
    }

    const handleMediaDragStart = (event) => {
      setIsDraggingMedia(true);
      setDraggingFromTicketId(event.detail?.fromTicketId ?? null);
    };

    const handleMediaDragEnd = () => {
      setIsDraggingMedia(false);
      setDraggingFromTicketId(null);
      setIsDragOver(false);
    };

    window.addEventListener(MEDIA_DRAG_EVENT_START, handleMediaDragStart);
    window.addEventListener(MEDIA_DRAG_EVENT_END, handleMediaDragEnd);

    return () => {
      window.removeEventListener(MEDIA_DRAG_EVENT_START, handleMediaDragStart);
      window.removeEventListener(MEDIA_DRAG_EVENT_END, handleMediaDragEnd);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      mediaCacheRef.current.clear();
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const isSameTicketSource =
    draggingFromTicketId &&
    String(draggingFromTicketId) === String(ticket.id);
  const isDropDisabled = ticket.status !== "open" || Boolean(isSameTicketSource);

  const isDarkTheme = theme.palette.type === "dark" || theme.mode === "dark";

  const getTagStyle = (color) => {
    if (color) {
      return {
        backgroundColor: `${color}15`,
        color,
      };
    }

    return {
      backgroundColor: isDarkTheme ? "rgba(255, 255, 255, 0.12)" : "#f5f5f5",
      color: isDarkTheme ? "#e0e0e0" : "#616161",
    };
  };

  const buildTicketPath = useCallback((targetTicket) => {
    if (!targetTicket) return "/tickets";
    if (targetTicket.id) {
      return `/tickets/${targetTicket.id}`;
    }
    if (targetTicket.uuid) {
      return `/tickets/${targetTicket.uuid}`;
    }
    return "/tickets";
  }, []);

  const handleAction = async (action, id) => {
    setLoading(true);
    try {
      const statusMap = { accept: "open", close: "closed", reopen: "open" };
      const { data: updatedTicket } = await api.put(`/tickets/${id}`, {
        status: statusMap[action],
        userId: user?.id,
        queueId: ticket?.queue?.id
      });
      const ticketData = updatedTicket || { ...ticket, status: statusMap[action] };

      if (statusMap[action] === "open") {
        invalidateDashboardCache();
      }

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:patched", { detail: { ticket: ticketData } })
        );
      }

      if (action === "accept") {
        const { data } = await api.get("/settings/");
        const setting = data.find(s => s.key === "sendGreetingAccepted");
        if (setting?.value === "enabled" && !ticket.isGroup) {
          await api.post(`/messages/${id}`, {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `*Mensagem Automática:*\n{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com seu atendimento!`
          });
        }
      }

      if (action === "close") {
        history.push(`/tickets/`);
      } else {
        setCurrentTicket({
          id: ticketData.id,
          uuid: ticketData.uuid,
          code: uuidv4()
        });

        if (statusMap[action] === "open" && typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("tickets:change-tab", {
              detail: { tab: "open", ticket: ticketData }
            })
          );
        }

        const path = buildTicketPath(ticketData);
        history.push(path, { ticket: ticketData });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveOutcome = async (outcome, extra = {}, options = {}) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/resolve`, {
        outcome,
        archive: true,
        ...(primaryOutcomeBoardId ? { kanbanBoardId: primaryOutcomeBoardId } : {}),
        ...extra
      });
      const ticketData = data?.ticket || { ...ticket, status: "closed", outcome };

      invalidateDashboardCache();

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:patched", { detail: { ticket: ticketData } })
        );
      }

      toast.success(outcome === "won" ? "Ticket marcado como ganho!" : "Ticket marcado como perda!");
      history.push("/tickets/");
    } catch (err) {
      toastError(err);
      if (options.rethrow) {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmLoss = async ({ kanbanBoardId, lossReasonId }) => {
    await handleResolveOutcome("lost", { kanbanBoardId, lossReasonId }, { rethrow: true });
  };

  const handleAvatarClick = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (localManualFlag) {
      return;
    }

    setLocalManualFlag(true);
    try {
      const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, { manualFlag: true });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:patched", {
            detail: { ticket: updatedTicket || { ...ticket, manualFlag: true } }
          })
        );
      }
    } catch (err) {
      setLocalManualFlag(false);
      toastError(err);
    }
  }, [ticket, localManualFlag]);

  const handleBrokenAvatar = useCallback(() => {
    const url = ticket?.contact?.profilePicUrl;
    if (!ticket?.id || !/^\/profilepicture\//.test(url || "") || navigator.onLine === false) return;
    const key = `${ticket.id}:${url}`;
    if ((brokenAvatarReports.get(key) || 0) > Date.now()) return;
    if (brokenAvatarReports.size >= 500) brokenAvatarReports.delete(brokenAvatarReports.keys().next().value);
    brokenAvatarReports.set(key, Date.now() + 60000);
    api.post(`/tickets/${ticket.id}/avatar-broken`, { url }, { timeout: 3000 }).catch(() => {});
  }, [ticket]);

  const updatePinMode = async (id, nextMode) => {
    if (!canPin) {
      return;
    }

    const previousMode = localPinMode;
    setLocalPinMode(nextMode);

    setLoading(true);
    try {
      const { data: updatedTicket } = await api.put(`/tickets/${id}`, {
        pinMode: nextMode
      });

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("ticket:patched", { detail: { ticket: updatedTicket } })
        );
      }
    } catch (err) {
      setLocalPinMode(previousMode);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePinClick = (id) => {
    if (pinClickTimerRef.current) {
      clearTimeout(pinClickTimerRef.current);
    }

    pinClickTimerRef.current = setTimeout(() => {
      const nextMode = localPinMode === "pinned"
        ? pinnedFromModeRef.current === "protected"
          ? "none"
          : "protected"
        : localPinMode === "protected"
          ? "none"
          : "protected";
      updatePinMode(id, nextMode);
      pinClickTimerRef.current = null;
    }, 220);
  };

  const handlePinDoubleClick = (id) => {
    if (pinClickTimerRef.current) {
      clearTimeout(pinClickTimerRef.current);
      pinClickTimerRef.current = null;
    }

    const nextMode = localPinMode === "pinned" ? "none" : "pinned";
    pinnedFromModeRef.current = nextMode === "pinned" ? localPinMode : null;
    updatePinMode(id, nextMode);
  };

  const handleForwardVcardOrLocation = useCallback(
    async (messageData, targetTicketId) => {
      const messageLabel = messageData.isVcard ? "Contato" : "Localização";
      const toastId = `forward-${messageData.messageId}`;

      try {
        toast.info(`Encaminhando ${messageLabel.toLowerCase()}...`, {
          autoClose: false,
          toastId,
        });

        // Para VCARD e Location, apenas enviar o body via API (não precisa de FormData/blob)
        await api.post(`/messages/${targetTicketId}`, {
          body: messageData.body || "",
          fromMe: true,
          // Preservar o mediaType original para que o backend processe corretamente
          ...(messageData.mediaType && { mediaType: messageData.mediaType })
        });

        toast.dismiss(toastId);
        toast.success(`${messageLabel} encaminhado com sucesso!`);
      } catch (err) {
        toast.dismiss(toastId);
        if (err?.response?.status === 403) {
          toast.error("Sem permissão para enviar mensagem neste ticket");
        } else {
          const normalizedMessage =
            typeof err?.message === "string" ? err.message.toLowerCase() : "";
          if (normalizedMessage.includes("network")) {
            toast.error("Erro de conexão. Verifique sua internet.");
          } else {
            toastError(err);
          }
        }
      }
    },
    []
  );

  const handleForwardMedia = useCallback(
    async (messageData, targetTicketId) => {
      const mediaLabel = getMediaLabel(messageData.mediaType);
      const toastId = `forward-${messageData.messageId}`;

      try {
        toast.info(`Encaminhando ${mediaLabel.toLowerCase()}...`, {
          autoClose: false,
          toastId,
        });

        let mediaBlob = mediaCacheRef.current.get(messageData.mediaUrl);
        if (!mediaBlob) {
          const { data } = await api.get(messageData.mediaUrl, {
            responseType: "blob",
          });
          // Validar que blob não está vazio ou corrompido
          if (!data || data.size === 0) {
            throw new Error("BLOB_EMPTY");
          }
          mediaBlob = data;
          mediaCacheRef.current.set(messageData.mediaUrl, data);
        }

        const desiredMimeType =
          messageData.mimeType || mediaBlob?.type || "application/octet-stream";
        const normalizedFileName = normalizeFileName(
          messageData.fileName,
          messageData.mediaUrl,
          "arquivo"
        );
        const finalFileName = ensureFileNameHasExtension(
          normalizedFileName,
          desiredMimeType
        );
        const uploadBlob =
          mediaBlob?.type === desiredMimeType || !mediaBlob?.slice
            ? mediaBlob
            : mediaBlob.slice(0, mediaBlob.size, desiredMimeType);

        // Se o corpo da mensagem for exatamente a legenda padrão de vídeo, enviar vazio
        const bodyToSend = messageData.body === "🎥 Vídeo" ? "" : (messageData.body || "");

        const formData = new FormData();
        formData.append("medias", uploadBlob, finalFileName || "media");
        formData.append("body", bodyToSend);
        formData.append("fromMe", "true");
        if (messageData.originalMediaType || messageData.mediaType) {
          formData.append(
            "mediaType",
            messageData.originalMediaType || messageData.mediaType
          );
        }
        if (messageData.isVoiceRecording) {
          formData.append("isVoiceRecording", "true");
        }

        await api.post(`/messages/${targetTicketId}`, formData);

        toast.dismiss(toastId);
        toast.success(`${mediaLabel} encaminhado com sucesso!`);
      } catch (err) {
        toast.dismiss(toastId);
        if (err?.response?.status === 413) {
          toast.error("Arquivo muito grande para ser encaminhado");
        } else if (err?.response?.status === 403) {
          toast.error("Sem permissão para acessar esta mídia");
        } else if (err?.response?.status === 404) {
          toast.error("Mídia não encontrada. O arquivo pode ter sido removido.");
        } else if (err?.message === "BLOB_EMPTY") {
          toast.error("Erro ao carregar mídia. Arquivo corrompido ou indisponível.");
        } else {
          const normalizedMessage =
            typeof err?.message === "string" ? err.message.toLowerCase() : "";
          if (normalizedMessage.includes("network")) {
            toast.error("Erro de conexão. Verifique sua internet.");
          } else {
            toastError(err);
          }
        }
      }
    },
    []
  );

  const handleDragEnter = useCallback(
    (event) => {
      if (
        !isDraggingMedia ||
        !Array.from(event.dataTransfer?.types || []).includes(
          MEDIA_DRAG_MIME_TYPE
        )
      ) {
        return;
      }

      if (isDropDisabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    },
    [isDraggingMedia, isDropDisabled]
  );

  const handleDragLeave = useCallback(
    (event) => {
      if (
        !isDraggingMedia ||
        !Array.from(event.dataTransfer?.types || []).includes(
          MEDIA_DRAG_MIME_TYPE
        )
      ) {
        return;
      }

      if (
        event.currentTarget &&
        event.relatedTarget &&
        event.currentTarget.contains(event.relatedTarget)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
    },
    [isDraggingMedia]
  );

  const handleDragOver = useCallback(
    (event) => {
      if (
        !isDraggingMedia ||
        !Array.from(event.dataTransfer?.types || []).includes(
          MEDIA_DRAG_MIME_TYPE
        )
      ) {
        return;
      }

      if (isDropDisabled) {
        event.dataTransfer.dropEffect = "none";
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = "copy";
    },
    [isDraggingMedia, isDropDisabled]
  );

  const handleDrop = useCallback(
    async (event) => {
      if (
        !Array.from(event.dataTransfer?.types || []).includes(
          MEDIA_DRAG_MIME_TYPE
        )
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);

      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(MEDIA_DRAG_HANDLED_IN_APP));
      }

      if (isDropDisabled) {
        toast.error("Ticket precisa estar aberto para receber mídias");
        return;
      }

      const payload = event.dataTransfer.getData(MEDIA_DRAG_MIME_TYPE);
      if (!payload) {
        return;
      }

      try {
        const messageData = JSON.parse(payload);

        // Verificar se é VCARD ou Location (não têm mediaUrl)
        const isVcardOrLocation = messageData?.isVcard || messageData?.isLocation;

        // Se não for VCARD/Location, precisa ter mediaUrl
        if (!isVcardOrLocation && !messageData?.mediaUrl) {
          toast.error("Não foi possível identificar a mídia selecionada.");
          return;
        }

        // Se for VCARD/Location, precisa ter body
        if (isVcardOrLocation && !messageData?.body) {
          toast.error("Não foi possível identificar o conteúdo da mensagem.");
          return;
        }

        if (String(messageData.fromTicketId) === String(ticket.id)) {
          toast.warning("Não é possível encaminhar para o mesmo ticket");
          return;
        }

        // Usar função apropriada dependendo do tipo
        if (isVcardOrLocation) {
          await handleForwardVcardOrLocation(messageData, ticket.id);
        } else {
          await handleForwardMedia(messageData, ticket.id);
        }
      } catch (err) {
        toastError(err);
      }
    },
    [handleForwardMedia, handleForwardVcardOrLocation, isDropDisabled, ticket.id]
  );

  const handleClick = async () => {
    // Permitir admin ou usuários com modo de visualização abrir tickets pendentes.
    if (ticket.status === "pending" && !canOpenPendingTickets) return;

    if (localManualFlag) {
      setLocalManualFlag(false);
      api.put(`/tickets/${ticket.id}`, { manualFlag: false })
        .then(({ data: updatedTicket }) => {
          if (typeof window !== "undefined") {
            window.dispatchEvent(
              new CustomEvent("ticket:patched", {
                detail: { ticket: updatedTicket || { ...ticket, manualFlag: false } }
              })
            );
          }
        })
        .catch((err) => {
          setLocalManualFlag(true);
          toastError(err);
        });
    }

    const path = buildTicketPath(ticket);
    const isSearchMatchJump = Boolean(searchMatch?.messageId);

    if (!isSearchMatchJump) {
      setCurrentTicket({ id: ticket.id, uuid: ticket.uuid, code: uuidv4() });
    }

    console.log('[TicketListItem] Clicking ticket', {
      ticketId: ticket.id,
      status: ticket.status,
      userProfile: user.profile,
      viewPendingTickets: user.viewPendingTickets,
      willBeReadonly: ticket.status === "pending" && canOpenPendingTickets
    });

    const navigationState = { ticket };
    if (isSearchMatchJump) {
      navigationState.jumpToMessageId = searchMatch.messageId;

      let resolvedJumpPageNumber = null;
      if (typeof searchMatch.pageNumber === "number") {
        resolvedJumpPageNumber = searchMatch.pageNumber;
      }

      if (resolvedJumpPageNumber) {
        navigationState.jumpPageNumber = resolvedJumpPageNumber;
      }

      navigationState.jumpSearchTerm = searchTerm || undefined;
      navigationState.jumpOrigin = "ticket-search-message";
    }

    const finalPath = searchMatch?.messageId
      ? `${path}?messageId=${searchMatch.messageId}`
      : path;

    history.push(finalPath, navigationState);
  };

  const handleOpenTransferModal = (e) => {
    e.stopPropagation();
    setTransferModalOpen(true);
  };

  const isSearchMatchJump = Boolean(searchMatch?.messageId);
  const urlMessageId = new URLSearchParams(location.search).get("messageId");
  const currentJumpMessageId = location.state?.jumpToMessageId || urlMessageId;

  let isSelected = false;
  if (ticketId && +ticketId === ticket.id) {
    if (isSearchMatchJump) {
      if (currentJumpMessageId) {
        isSelected = String(currentJumpMessageId) === String(searchMatch?.messageId);
      } else {
        isSelected = false;
      }
    } else {
      isSelected = true;
    }
  }

  const { contact, unreadMessages, updatedAt, lastMessageAt, lastMessage, whatsapp, user: ticketUser, queue, tags = [] } = ticket;
  const hiddenTagsIndicatorColor = theme?.palette?.grey?.[500] || "#9e9e9e";
  const badges = [
    ...(whatsapp?.name ? [{ key: "connection", name: whatsapp.name, style: { backgroundColor: "#25D36620", color: "#128C7E" } }] : []),
    ...(queue?.name ? [{ key: "queue", name: queue.name, style: getTagStyle(queue.color) }] : []),
    ...tags.map(tag => ({ key: `tag-${tag.id}`, name: tag.name, style: getTagStyle(tag.color) })),
  ];
  const closeLabel = hasOutcomeTracking ? "Perda" : i18n.t("ticketsList.buttons.closed");
  const closeAction = {
    key: hasOutcomeTracking ? "lost" : "close", label: closeLabel, icon: <Close className={classes.icon} />,
    onClick: event => {
      event.stopPropagation();
      if (hasOutcomeTracking) setLossModalOpen(true);
      else handleAction("close", ticket.id);
    },
  };
  const quickActions = ticket.status === "closed"
    ? [{ key: "reopen", label: i18n.t("ticketsList.buttons.reopen"), icon: <Replay className={classes.icon} />, onClick: event => { event.stopPropagation(); handleAction("reopen", ticket.id); } }]
    : ticket.status === "pending"
      ? [{ key: "accept", label: i18n.t("ticketsList.buttons.accept"), icon: <Done className={classes.icon} />, buttonClass: "acceptBtn", onClick: event => { event.stopPropagation(); handleAction("accept", ticket.id); } }, closeAction]
      : [
        ...(hasOutcomeTracking ? [{ key: "won", label: "Ganho", icon: <Done className={classes.icon} />, onClick: event => { event.stopPropagation(); handleResolveOutcome("won"); } }] : []),
        { key: "transfer", label: "Transferir", title: "Transferir Ticket", icon: <SwapHoriz className={classes.icon} />, buttonClass: "transferBtn", onClick: handleOpenTransferModal },
        closeAction,
      ];
  const draft = getDraft("ticket", ticket.id);
  const hasDraft = !isSelected && Boolean(draft?.body?.trim());
  const showDropIcon = isDragOver && !isDropDisabled;
  const searchSnippet =
    !hasDraft && typeof searchMatch?.snippet === "string"
      ? searchMatch.snippet.trim()
      : "";
  const displayAt = resolveTicketDisplayAt({ lastMessageAt, updatedAt }, searchMatch);
  const searchTerm = typeof searchMatch?.term === "string" ? searchMatch.term.trim() : "";
  const pinTitle = localPinMode === "pinned"
    ? "Fixado no topo e protegido contra encerramento automático"
    : localPinMode === "protected"
      ? "Protegido contra encerramento automático"
      : "Clique para proteger; duplo clique para fixar";
  const pinColor = localPinMode === "pinned" ? "#14A45C" : "#9e9e9e";

  const renderHighlightedText = useCallback(
    (text, term) => {
      const safeText = typeof text === "string" ? text : "";
      const safeTerm = typeof term === "string" ? term.trim() : "";
      if (!safeText || !safeTerm) {
        return safeText;
      }

      const escaped = safeTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "ig");
      const parts = safeText.split(regex);
      const normalized = safeTerm.toLowerCase();

      return parts.map((part, index) => {
        const isMatch = part.toLowerCase() === normalized;
        if (!isMatch) {
          return <span key={`t-${index}`}>{part}</span>;
        }
        return (
          <span key={`h-${index}`} className={classes.searchHighlight}>
            {part}
          </span>
        );
      });
    },
    [classes.searchHighlight]
  );

  return (
    <>
      <ListItem
        button
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className={clsx(classes.ticket, {
          selected: isSelected,
          [classes.ticketDragOver]: isDragOver,
          [classes.ticketDropDisabled]: isDraggingMedia && isDropDisabled,
        })}
      >
        {showDropIcon && (
          <div className={classes.ticketDragOverlay}>
            <CloudUpload fontSize="large" />
            <span>Encaminhar aqui</span>
          </div>
        )}
        {loading && <CircularProgress size={16} style={{ position: 'absolute', top: '50%', left: '50%', marginTop: -8, marginLeft: -8 }} />}

        <ListItemAvatar className={classes.listItemAvatar}>
          {unreadMessages > 0 && (
            <div
              className={classes.customBadge}
              style={unreadMessages > 9 ? {
                backgroundColor: "#d32f2f",
                transform: "scale(1.1)"
              } : {}}
            >
              {unreadMessages > 99 ? '99+' : unreadMessages}
            </div>
          )}
          {!unreadMessages && localManualFlag && (
            <div className={classes.manualFlagBadge} />
          )}
          <div
            onClick={handleAvatarClick}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ display: "inline-block", cursor: "pointer" }}
          >
            <ContactAvatar
              src={contact?.profilePicUrl}
              onImageError={avatarRefreshEnabled && /^\/profilepicture\//.test(contact?.profilePicUrl || "") ? handleBrokenAvatar : undefined}
              alt={contact?.name || contact?.number || "Contato"}
              className={clsx(classes.avatar, {
                [classes.avatarPulsingRed]: getTaskStatus(ticket.tasks) === "overdue",
                [classes.avatarPulsingYellow]: getTaskStatus(ticket.tasks) === "upcoming",
              })}
            />
          </div>
          <TicketAssigneeBadge ticket={ticket} />
        </ListItemAvatar>

        <ListItemText
          disableTypography
          primary={
            <div className={classes.header}>
              <Typography className={classes.contactName}>
                {contact.name}
                {ticket.chatbot && <Android className={classes.icon} style={{ color: "#ff9800", fontSize: "0.9rem" }} />}
              </Typography>
              <div style={{ display: "flex", alignItems: "center" }}>
                {canPin && (
                  <Tooltip title={pinTitle}>
                    <IconButton
                      style={{
                        width: 20,
                        height: 20,
                        zIndex: 10,
                        padding: 0,
                      }}
                      onMouseDown={(e) => e.stopPropagation()} // Importante para evitar ripple do ListItem
                      onClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation(); // Forçar parada total
                        handlePinClick(ticket.id);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        handlePinDoubleClick(ticket.id);
                      }}
                    >
                      <PushPinIcon
                        fontSize="small"
                        htmlColor={pinColor}
                        outlined={localPinMode === "none"}
                        style={{
                          fontSize: 16
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography className={classes.time} style={{ marginLeft: 2 }}>
                  {displayAt && (
                    isSameDay(parseISO(displayAt), new Date())
                      ? format(parseISO(displayAt), "HH:mm")
                      : format(parseISO(displayAt), "dd/MM")
                  )}
                </Typography>
              </div>
            </div>
          }
          secondary={
            <>
              <Typography className={`${classes.lastMessage} ${hasDraft ? classes.draftMessage : ""}`}>
                {hasDraft ? (
                  <>
                    <span className={classes.draftLabel}>
                      {i18n.t("drafts.label")}:
                    </span>
                    <span className={classes.draftPreview}>
                      <MarkdownWrapper>{draft.body}</MarkdownWrapper>
                    </span>
                  </>
                ) : searchSnippet ? (
                  renderHighlightedText(searchSnippet, searchTerm)
                ) : lastMessage?.includes("data:image/png;base64") ? (
                  "📍 Localização"
                ) : lastMessage?.includes("BEGIN:VCARD") ? (
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <ContactPhone fontSize="small" style={{ marginRight: 4, fontSize: 16, color: '#666' }} />
                    {(() => {
                      const fnMatch = lastMessage.match(/FN:(.*)/);
                      return fnMatch ? fnMatch[1].trim() : "Contato";
                    })()}
                  </span>
                ) : lastMessage ? (
                  <MarkdownWrapper>{lastMessage}</MarkdownWrapper>
                ) : (
                  ""
                )}
              </Typography>

              <TicketBadgeList badges={badges} actionsRef={actionsRef} classes={classes} indicatorStyle={getTagStyle(hiddenTagsIndicatorColor)} />
            </>
          }
        />

        <div className={classes.actions} ref={actionsRef}>
          {commercialEnabled
            ? <CommercialQuickActions classes={classes} ticket={ticket} onAction={handleAction} onTransfer={handleOpenTransferModal} />
            : <TicketQuickActions classes={classes} actions={quickActions} />}
        </div>
      </ListItem >

      {transferModalOpen && (
        <TransferTicketModalCustom
          modalOpen={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          ticketid={ticket.id}
        />
      )
      }
      <RegisterLossModal
        open={lossModalOpen}
        onClose={() => setLossModalOpen(false)}
        ticketId={ticket.id}
        boardId={primaryOutcomeBoardId}
        boards={outcomeBoards}
        onConfirm={handleConfirmLoss}
      />
    </>
  );
};

export default TicketListItemCustom;
